
import Vue from "vue";
import API from "@/api/API";
import ImageViewer from "@/components/viewers/Image.vue";
import tablePagination from "@/mixins/table-pagination";
import tableFilters from "@/mixins/table-filters";
import { parseArrayParams } from "@/services/helpers";
import { Dictionary } from "vue-router/types/router";
import { APPLICATION_STATUSES } from "@/static/constants";
import headers from "../config/headers.json";

export default Vue.extend({
  name: "applicationRequests",

  components: { ImageViewer },

  mixins: [tablePagination, tableFilters],

  data: () => ({
    APPLICATION_STATUSES,
    headers,
    parseArrayParams,
    items: [] as Array<any>,
    filter: {
      quick_filter: "",
      admission_year: null,
    } as any,
    showSearch: false as boolean,
    loading: false as boolean,
    queryString: "" as string,
    availableStatuses: [] as any,
    isDisabled: false as boolean,
    pagination: {} as any
  }),

  computed: {
    admissionYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let year = 2021; year <= currentYear; year++) {
        years.push({ value: String(year), text: String(year) });
      }
      return years;
    }
  },

  async beforeRouteEnter(to, from, next) {
    try {
      const params = {
        filter: to.query,
        page: {
          number: Number(to.params.page),
          size: Number(localStorage.getItem("applications_per_page")) || 20,
        },
      };

      const response = await API.applicationRequests().getData(params);

      next((vm) => {
        vm.setFilters(params.filter);
        vm.setServerResponse(response);
        vm.getAvailableStatuses();
        vm.$data.pagination = params.page
      });
    } catch (e) {
      await this.$store.dispatch("alert/showError", e.message);
    }
  },

  mounted() {
    const currentYear = new Date().getFullYear().toString()

    this.filter.admission_year = [currentYear];
    this.applyFilters();
  },

  methods: {
    async loadData(): Promise<void> {
      this.loading = true;

      try {
        const response = await this.$API.applicationRequests().getData({
          filter: this.parseArrayParams(this.filter),
          page: this.pagination,
          sort: this.getSortParams(),
        });

        this.setServerResponse(response);
      } catch (e) {
        await this.$store.dispatch("alert/showError", e.message);
      }
      this.loading = false;
    },
    setServerResponse({ items }: any) {
      this.items = items.data;
      this.totalItems = items.total;
    },
    setFilters(filter: Dictionary<string | (string | null)[]>): void {
      const newFilters = this.getAvailableFilters(filter);

      if (Object.keys(newFilters).length) {
        this.showFilters = true;
        this.showSearch = true;
      }

      this.filter = newFilters;
      this.changeActiveFilters();
    },
    toggleSearch(): void {
      this.showSearch = !this.showSearch;

      if (this.showSearch) {
        this.$nextTick(() => {
          (this.$refs.search as any).focus();
        });
      }
    },
    search() {
      if (this.debounced) {
        this.debounced.cancel();
      }
      this.debounced = this.debounce(500, this.applyFilters);
      this.debounced();
    },
    async cancelApplication(id: number): Promise<void> {
      try {
        await this.$API.applicationRequests().cancel(id);
        await this.loadData();
        await this.$store.dispatch(
          "alert/showSuccess",
          "Cererea a fost refuzata"
        );
      } catch (e) {
        await this.$store.dispatch("alert/showError", e.message);
      }
    },
    async getAvailableStatuses(): Promise<void> {
      try {
        this.availableStatuses = await this.$API
          .applicationStatuses()
          .getList();
      } catch (e) {
        await this.$store.dispatch("alert/showError", e.message);
      }
    },
    async setStatusVerified(id: number): Promise<void> {
      try {
        await this.$API.applicationRequests().setStatusVerify(id);
        await this.$store.dispatch(
          "alert/showSuccess",
          this.$t("applicationRequests.alert.verified_success")
        );
        await this.loadData();
      } catch (e) {
        await this.$store.dispatch("alert/showError", e.message);
      }
    },
    async downloadFile(file: any) {
      if (file) {
        const element = document.createElement("a");
        element.setAttribute("href", file.url);
        element.setAttribute("download", file.name);
        element.setAttribute("target", "_blank");
        element.click();
      }
    },
    async printResults(id: number) {
      try {
        const file = await this.$API.applications().printEnrolled(id);

        await this.downloadFile(file);
      } catch (e) {
        await this.$store.dispatch("alert/showError", e.message);
      }
    },
    async getRatingTable() {
      this.isDisabled = true;

      try {
        const response = await this.$API
          .applicationRequests()
          .getRatingTable({ responseType: "blob" });

        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.target = "_blank";
        link.download = `rating.xlsx`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (e) {
        await this.$store.dispatch("alert/showError", e.message);
      }

      this.isDisabled = false;
    },
    async changePaginationSize(): Promise<void> {
      localStorage.setItem("applications_per_page", this.pagination.size);

      await this.loadData();
    }
  },
});
